import api from './api';
import type { PurchaseOrderType } from './purchasing.service';
import type { SyncBadge } from './sales-invoice.service';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface ListResponse<T> {
  items: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface InternalSalesInvoiceSource {
  id: string;
  invoice_number: string;
  tax_authority_invoice_id?: string;
  invoice_date: string;
  store_id: string;
  store_code: string;
  store_name: string;
  customer_name: string;
  net_amount: number;
}

export interface InternalSalesInvoiceSourceList {
  items: InternalSalesInvoiceSource[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

type ListMeta = ListResponse<PurchaseInvoice>['meta'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isPurchaseInvoiceArray(value: unknown): value is PurchaseInvoice[] {
  return Array.isArray(value);
}

function normalizeMeta(value: unknown, fallbackTotal: number): ListMeta {
  if (isRecord(value)) {
    return {
      page: Number(value.page ?? 1),
      limit: Number(value.limit ?? fallbackTotal),
      total: Number(value.total ?? fallbackTotal),
      totalPages: Number(value.totalPages ?? value.total_pages ?? 1),
    };
  }
  return { page: 1, limit: fallbackTotal, total: fallbackTotal, totalPages: 1 };
}

export function normalizePurchaseInvoiceListResponse(response: unknown): ApiResponse<ListResponse<PurchaseInvoice>> {
  const root = isRecord(response) ? response : {};
  const payload = root.data;

  if (isPurchaseInvoiceArray(payload)) {
    return {
      success: root.success !== false,
      data: { items: payload, meta: normalizeMeta(root.meta, payload.length) },
      message: typeof root.message === 'string' ? root.message : undefined,
    };
  }

  if (isRecord(payload)) {
    const nestedItems = isPurchaseInvoiceArray(payload.items)
      ? payload.items
      : isPurchaseInvoiceArray(payload.data)
        ? payload.data
        : [];
    return {
      success: root.success !== false,
      data: { items: nestedItems, meta: normalizeMeta(payload.meta ?? root.meta, nestedItems.length) },
      message: typeof root.message === 'string' ? root.message : undefined,
    };
  }

  return {
    success: root.success !== false,
    data: { items: [], meta: normalizeMeta(root.meta, 0) },
    message: typeof root.message === 'string' ? root.message : undefined,
  };
}

export type InvoiceReceiptStatus =
  | 'with_invoice'
  | 'without_invoice'
  | 'invoice_later'
  | 'goods_in_transit';

export interface PurchaseInvoice {
  id: string;
  invoice_no: string;
  purchase_invoice_number?: string;
  supplier_id: string;
  store_id?: string;
  store_name?: string;
  supplier_name: string;
  supplier_tax_code?: string;
  invoice_date: string;
  supplier_invoice_number?: string;
  supplier_invoice_date?: string;
  supplier_invoice_symbol?: string;
  supplier_invoice_form_code?: string;
  invoice_category?: 'goods' | 'service';
  items?: PurchaseInvoiceItem[];
  purchase_type?: PurchaseOrderType;
  payment_method?: 'cash' | 'bank_transfer' | 'credit';
  payment_status?: 'unpaid' | 'partial' | 'paid';
  bank_ref?: string;
  currency?: string;
  exchange_rate?: number;
  is_landed_cost?: boolean;
  invoice_receipt_status?: InvoiceReceiptStatus;
  purchase_order_id?: string;
  purchase_order_code?: string;
  purchase_order_status?: string;
  linked_goods_receipt_id?: string;
  linked_goods_receipt_ids?: string[];
  linked_goods_receipts?: Array<{
    _id: string;
    receipt_code?: string;
    status?: string;
  }>;
  stock_transfer_status?: string | null;
  stock_transfer_id?: string | null;
  accounting_date?: string;
  document_date?: string;
  total_amount: number;
  tax_amount: number;
  final_amount: number;
  discount_total?: number;
  discount_amount?: number;
  status: 'draft' | 'posted' | 'cancelled';
  import_cost_status?: 'pending' | 'completed' | 'incomplete';
  import_cost_errors?: Array<{
    component?: string;
    account_code?: string;
    code?: string;
    message: string;
    occurred_at: string;
  }>;
  is_matched?: boolean;
  matched_gov_invoice_id?: string;
  sync?: SyncBadge;
  amis_sync_blocked_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface PurchaseInvoiceItem {
  description?: string;
  name?: string;
  product_name?: string;
  quantity?: unknown;
  unit_price?: unknown;
  taxable_amount?: unknown;
  line_total?: unknown;
  total_amount?: unknown;
  amount?: unknown;
  vat_rate?: unknown;
  tax_rate?: unknown;
  vat_amount?: unknown;
  tax_amount?: unknown;
  account_code?: string;
}

export interface PurchaseCostDocumentAllocation {
  purchase_invoice_id: string;
  purchase_invoice_number: string;
  status: 'draft' | 'posted';
  allocated_amount: number;
  pending_amount: number;
}

export interface PurchaseCostDocument extends PurchaseInvoice {
  allocated_amount?: number;
  remaining_allocatable_amount?: number;
  is_allocated?: boolean;
  is_fully_allocated?: boolean;
  allocated_count?: number;
  pending_allocated_amount?: number;
  pending_allocated_count?: number;
  allocated_invoices?: PurchaseCostDocumentAllocation[];
}

export interface PurchaseCostDocumentList {
  items: PurchaseCostDocument[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PurchaseInvoiceListParams {
  page?: number;
  limit?: number;
  status?: string;
  payment_status?: string;
  supplier_id?: string;
  purchase_order_id?: string;
  store_id?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
  is_matched?: boolean;
  invoice_category?: 'goods' | 'service';
  is_landed_cost?: boolean;
  invoice_receipt_status?: InvoiceReceiptStatus;
}

export interface CreatePurchaseInvoiceDto {
  supplier_id: string;
  source_sales_invoice_id?: string;
  store_id?: string;
  supplier_invoice_number?: string;
  invoice_date?: string;
  supplier_invoice_date?: string;
  supplier_invoice_symbol?: string;
  supplier_invoice_form_code?: string;
  invoice_category: 'goods' | 'service';
  purchase_type?: PurchaseOrderType;
  currency?: string;
  exchange_rate?: number | string;
  is_landed_cost?: boolean;
  invoice_receipt_status?: InvoiceReceiptStatus;
  accounting_date?: string;
  document_date?: string;
  reference_no?: string;
  items: Array<{
    product_id?: string;
    variant_id?: string;
    warehouse_id?: string;
    purchase_order_item_index?: number;
    description: string;
    /** Import goods: preserve the PO quantity breakdown when editing. */
    ordered_quantity?: number | string;
    foc_quantity?: number | string;
    foc_percent?: number | string;
    discount_quantity_percent?: number | string;
    discount_quantity?: number | string;
    weight_kg?: number | string;
    volume_m3?: number | string;
    quantity: number | string;
    unit_price: number | string;
    tax_rate?: number;
    tax_amount?: number;
    vat_rate?: number | string;
    vat_rate_id?: string;
    vat_rate_code?: string;
    vat_source?: string;
    tax_industry_group_id?: string;
    hkd_industry_tax_rate_id?: string;
    vat_conversion_rule_id?: string;
    vat_amount?: number | string;
    line_discount_rate?: number | string;
    line_discount_amount?: number | string;
    account_code?: string;
    vat_account_code?: string;
    cost_center_id?: string;
    total_amount?: number;
    // Import-only — mirrors New PI's "Thuế" tab
    customs_exchange_rate?: number | string;
    customs_taxable_amount?: number | string;
    import_duty_rate?: number | string;
    import_duty_amount?: number | string;
    import_duty_account_code?: string;
    excise_tax_rate?: number | string;
    excise_tax_amount?: number | string;
    excise_tax_account_code?: string;
    import_vat_rate?: number | string;
    import_vat_amount?: number | string;
    import_vat_account_code?: string;
    import_vat_offset_account_code?: string;
    pre_customs_cost_amount?: number | string;
    pre_customs_credit_account_code?: string;
    inward_cost_amount?: number | string;
    inward_cost_credit_account_code?: string;
  }>;
  pending_cost_document_allocations?: Array<{
    cost_document_id: string;
    component: 'pre_customs' | 'inward_cost';
    total_amount: number | string;
    allocation_method: 'by_qty' | 'by_value' | 'by_weight' | 'by_volume';
    line_allocations?: Array<{ line_index: number; amount: number | string }>;
  }>;
  total_amount: number | string;
  total_vat?: number | string;
  tax_amount?: number;
  final_amount?: number;
  payment_method?: 'cash' | 'bank_transfer' | 'credit';
  payment_status?: 'unpaid' | 'partial' | 'paid';
  bank_ref?: string;
  notes?: string;
  purchase_staff_id?: string;
  discount_method?: string;
  discount_percent?: number;
  discount_amount?: number;
  invoice_discount_basis?: 'percent' | 'amount';
  payment_terms?: string;
  credit_days?: number;
  due_date?: string;
  ar_account_code?: string;
  purchase_order_id?: string;
  linked_goods_receipt_id?: string;
  linked_goods_receipt_ids?: string[];
}

export interface AllocateCostItemDto {
  cost_document_id?: string;
  description: string;
  total_amount: number;
  allocation_method: 'by_qty' | 'by_value' | 'by_weight' | 'by_volume';
  internal_cost?: boolean;
  internal_source_key?: string;
  line_allocations?: Array<{ line_index: number; amount: number }>;
}

export interface AllocateCostDto {
  cost_items: AllocateCostItemDto[];
}

export interface CreatePurchaseInvoiceFromReceiptDto {
  supplier_invoice_number: string;
  supplier_invoice_date: string;
  supplier_invoice_symbol?: string;
  supplier_invoice_form_code?: string;
  total_vat?: string;
  payment_status?: 'unpaid' | 'partial' | 'paid';
  payment_method?: 'cash' | 'bank_transfer';
  bank_ref?: string;
  auto_post?: boolean;
}

/**
 * Service cho hóa đơn mua hàng (Purchase Invoices)
 */
export const purchaseInvoiceService = {
  getInternalSalesInvoiceSources(params: {
    supplier_id: string;
    store_id?: string;
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<ApiResponse<InternalSalesInvoiceSourceList>> {
    return api.get('/accounting/purchase-invoices/internal-sales-sources', { params }).then((r) => r.data);
  },

  /**
   * Lấy danh sách hóa đơn mua
   */
  getPurchaseInvoices(params: PurchaseInvoiceListParams): Promise<ApiResponse<ListResponse<PurchaseInvoice>>> {
    return api.get('/accounting/purchase-invoices', { params }).then((r) => normalizePurchaseInvoiceListResponse(r.data));
  },

  /**
   * Lấy chi tiết hóa đơn mua
   */
  getPurchaseInvoiceDetail(id: string): Promise<ApiResponse<PurchaseInvoice>> {
    return api.get(`/accounting/purchase-invoices/${id}`).then((r) => r.data);
  },

  /**
   * Tạo hóa đơn mua mới
   */
  createPurchaseInvoice(data: CreatePurchaseInvoiceDto): Promise<ApiResponse<PurchaseInvoice>> {
    return api.post('/accounting/purchase-invoices', data).then((r) => r.data);
  },

  createFromGoodsReceipt(
    receiptId: string,
    data: CreatePurchaseInvoiceFromReceiptDto,
  ): Promise<ApiResponse<PurchaseInvoice>> {
    return api
      .post(`/accounting/purchase-invoices/from-goods-receipt/${receiptId}`, data)
      .then((r) => r.data);
  },

  /**
   * Cập nhật hóa đơn mua
   */
  updatePurchaseInvoice(id: string, data: Partial<CreatePurchaseInvoiceDto>): Promise<ApiResponse<PurchaseInvoice>> {
    return api.put(`/accounting/purchase-invoices/${id}`, data).then((r) => r.data);
  },

  /**
   * Xóa hóa đơn mua
   */
  deletePurchaseInvoice(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/purchase-invoices/${id}`).then((r) => r.data);
  },

  downloadPdf(id: string): Promise<Blob> {
    return api.get<Blob>(`/accounting/purchase-invoices/${id}/print`, {
      params: { format: 'pdf' },
      responseType: 'blob',
    }).then((r) => r.data);
  },

  /**
   * Post hóa đơn mua
   */
  postPurchaseInvoice(id: string): Promise<ApiResponse<PurchaseInvoice>> {
    return api.post(`/accounting/purchase-invoices/${id}/post`, {}, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }).then((r) => r.data);
  },

  retryImportCostComponents(id: string): Promise<ApiResponse<Array<{ component: string; journalEntryId: string }>>> {
    return api.post(`/accounting/purchase-invoices/${id}/import-cost/retry`, {}, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }).then((r) => r.data);
  },

  /**
   * Thử lại đồng bộ AMIS cho hoá đơn mua (TCR-2145-S8)
   */
  retrySync(id: string): Promise<ApiResponse<unknown>> {
    return api.post(`/accounting/purchase-invoices/${id}/accounting-sync/retry`, {}, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }).then((r) => r.data);
  },

  /**
   * Cập nhật & đồng bộ lại AMIS cho hoá đơn mua (TCR-2145-S8)
   */
  resync(id: string): Promise<ApiResponse<unknown>> {
    return api.post(`/accounting/purchase-invoices/${id}/accounting-sync/resync`, {}, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }).then((r) => r.data);
  },

  /**
   * Ghi nhận thanh toán cho hóa đơn mua
   */
  createPayment(id: string, data: {
    amount: number;
    payment_date: string;
    payment_method: 'cash' | 'bank_transfer';
    fund_account_id: string;
    store_id?: string;
    description?: string;
  }): Promise<ApiResponse<Record<string, unknown>>> {
    return api.post(`/accounting/purchase-invoices/${id}/payments`, data, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }).then((r) => r.data);
  },

  listPayments(id: string): Promise<ApiResponse<Array<Record<string, unknown>>>> {
    return api.get(`/accounting/purchase-invoices/${id}/payments`).then((r) => r.data);
  },

  getRemainingAmount(id: string): Promise<ApiResponse<{ remaining: number }>> {
    return api.get(`/accounting/purchase-invoices/${id}/payments/remaining`).then((r) => r.data);
  },

  unpostPayment(id: string, paymentId: string, reason: string): Promise<ApiResponse<void>> {
    return api.post(`/accounting/purchase-invoices/${id}/payments/${paymentId}/unpost`, { reason }, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }).then((r) => r.data);
  },

  /**
   * Phân bổ chi phí mua hàng (landed cost)
   */
  allocateCost(id: string, data: AllocateCostDto): Promise<ApiResponse<PurchaseInvoice>> {
    return api.post(`/accounting/purchase-invoices/${id}/allocate-cost`, data).then((r) => r.data);
  },

  finalizeCostDocumentLinks(id: string): Promise<ApiResponse<{ linkedCount: number; linkedAmount: number }>> {
    return api.post(`/accounting/purchase-invoices/${id}/finalize-cost-document-links`, {}).then((r) => r.data);
  },

  /**
   * Lấy danh sách chứng từ chi phí mua hàng để chọn phân bổ cho PINV nhập khẩu
   */
  getCostDocuments(params: { supplier_id?: string; store_id?: string; exclude_invoice_id?: string; limit?: number; search?: string; allocation_status?: 'unallocated' | 'allocated' | 'all' }): Promise<ApiResponse<PurchaseCostDocumentList>> {
    return api.get('/accounting/purchase-invoices', {
      params: {
        landed_cost_documents: true,
        status: 'posted',
        limit: params.limit || 50,
        supplier_id: params.supplier_id,
        store_id: params.store_id,
        exclude_invoice_id: params.exclude_invoice_id,
        search: params.search,
        account_prefix: '156',
        allocation_status: params.allocation_status || 'unallocated',
      },
    }).then((r) => r.data);
  },

  /**
   * Lấy danh sách hóa đơn mua chưa match (cho chức năng match với gov invoice)
   */
  getUnmatchedInvoices(params: { supplier_tax_code?: string; amount?: number; limit?: number }): Promise<ApiResponse<ListResponse<PurchaseInvoice>>> {
    return api.get('/accounting/purchase-invoices', {
      params: {
        ...params,
        is_matched: false,
        status: 'posted',
        limit: params.limit || 10,
      },
    }).then((r) => r.data);
  },

  /**
   * Lấy chi tiết nhà cung cấp
   */
  getSupplierDetail(id: string): Promise<Record<string, unknown>> {
    return api.get(`/suppliers/${id}`).then((r) => r.data);
  },
};
